import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Progress } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import useUpdateSubjects from '@/api/useUpdateSubjects.ts';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import { ArrowDownIcon } from '@heroicons/react/20/solid';

const AVAILABLE_SUBJECTS = [
  'English Literature',
  'Mathematics',
  'Combined Science (Trilogy: double award)',
  'Separate Sciences (Biology, Chemistry, Physics)',
  'History',
  'Geography',
  'Religious Studies (RS)',
  'Business Studies',
  'French',
  'German',
  'Spanish',
  'Design and Technology (DT)',
  'Food Preparation and Nutrition',
  'Engineering',
  'Computer Science',
  'Economics',
  'Physical Education (PE)',
];

const GRADES = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

const EXAM_BOARDS = [
  { value: 'Pearson Edexcel', label: 'Pearson Edexcel (the only currently supported)' },
  { value: 'AQA', label: 'AQA' },
  { value: 'OCR', label: 'OCR' },
  { value: 'Eduqas (WJEC)', label: 'Eduqas (WJEC)' },
];

interface OnboardingData {
  primarySubject: string;
  otherSubjects: string[];
  examBoard: string;
  predictedGrade?: string;
  aspirationalGrade?: string;
}

const steps = ['choose-subject', 'choose-board', 'predicted-grade', 'aspirational-grade'] as const;

type OnboardingSchema = OnboardingData;

const StudentOnboardingPage: FC = () => {
  return (
    <Stepper.Wrapper steps={steps} initialStep="choose-subject">
      <Stepper.Step<typeof steps> step="choose-subject">
        <StepChooseSubject />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="choose-board">
        <StepChooseBoard />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="predicted-grade">
        <StepPredictedGrade />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="aspirational-grade">
        <StepAspirationalGrade />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default StudentOnboardingPage;

function StepChooseSubject() {
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingSchema>();
  const [primarySubject, setPrimarySubject] = useState(contextData?.primarySubject || 'English Language');
  const [otherSubjects, setOtherSubjects] = useState<string[]>(contextData?.otherSubjects || []);
  const { mutateAsync: updateSubjects, isPending } = useUpdateSubjects();

  const handleToggleSubject = (subject: string) => {
    setOtherSubjects((prev) => (prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject]));
  };

  const handleNext = () => {
    void (async () => {
      try {
        const allSubjects = [primarySubject, ...otherSubjects];
        await updateSubjects({ subjects: allSubjects });
        setContextData({ primarySubject, otherSubjects, ...contextData });
        goToStep('choose-board');
      } catch (error) {
        console.error('Failed to save subjects:', error);
        setContextData({ primarySubject, otherSubjects, ...contextData });
        goToStep('choose-board');
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <div className="w-full max-w-[600px] flex flex-col gap-8 px-6">
        {/* Progress bar */}
        <div className="flex gap-2">
          <Progress className="flex-1 h-2" color="secondary" value={25} max={100} />
        </div>

        <div className="flex flex-col gap-6">
          <h1 className="text-2xl font-varela font-normal leading-[117%]">Choose subject</h1>

          {/* Primary subject dropdown */}
          <div className="flex flex-col gap-2">
            <label className="text-sm">
              Subjects <span className="italic">(Note: Only English is available for now.)</span>
            </label>
            <div className="relative">
              <SelectInput
                value={primarySubject}
                onChange={(value) => setPrimarySubject(value)}
                options={[{ value: 'English Language', label: 'English Language' }]}
                labelClassName="[&>svg]:hidden"
                bordered
                readOnly
              />
              <ArrowDownIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
            </div>
          </div>

          {/* Other subjects */}
          <div className="flex flex-col gap-4">
            <div>
              <h2 className="text-lg font-medium">Other subjects you need help with</h2>
              <p className="text-sm text-text-primary">You can still tick others to let us know what to add next!</p>
            </div>

            <div className="flex flex-col gap-3 max-h-64 overflow-y-auto">
              {AVAILABLE_SUBJECTS.map((subject) => (
                <label key={subject} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={otherSubjects.includes(subject)}
                    onChange={() => handleToggleSubject(subject)}
                    className="checkbox checkbox-sm"
                  />
                  <span className="text-base">{subject}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Button */}
          <div className="mt-4">
            <Button onClick={handleNext} color="primary" className="w-full" disabled={isPending}>
              {isPending ? 'Saving...' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </BrandCardSideContent>
  );
}

function StepChooseBoard() {
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingSchema>();
  const [examBoard] = useState(contextData?.examBoard || 'Pearson Edexcel');

  const handleNext = () => {
    setContextData({ ...contextData, examBoard });
    goToStep('predicted-grade');
  };

  return (
    <BrandCardSideContent>
      <div className="w-full max-w-[600px] flex flex-col gap-8 px-6">
        {/* Progress bar */}
        <div className="flex gap-2">
          <Progress className="flex-1 h-2" color="secondary" value={50} max={100} />
        </div>

        <div className="flex flex-col gap-6">
          <h1 className="text-2xl font-varela font-normal leading-[117%]">Select Exam Board</h1>

          {/* Exam boards radio buttons */}
          <div className="flex flex-col gap-3">
            {EXAM_BOARDS.map((board) => (
              <label key={board.value} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="examBoard"
                  value={board.value}
                  checked={examBoard === board.value}
                  readOnly
                  disabled={board.value !== 'Pearson Edexcel'}
                  className="radio radio-primary"
                />
                <span className={board.value !== 'Pearson Edexcel' ? 'text-gray-400' : ''}>{board.label}</span>
              </label>
            ))}
          </div>

          {/* Button */}
          <div className="mt-4">
            <Button onClick={handleNext} color="primary" className="w-full">
              Next
            </Button>
          </div>
        </div>
      </div>
    </BrandCardSideContent>
  );
}

function StepPredictedGrade() {
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingSchema>();
  const [predictedGrade, setPredictedGrade] = useState(contextData?.predictedGrade || '');
  const { mutateAsync: updateSubjects, isPending } = useUpdateSubjects();

  const handleNext = () => {
    void (async () => {
      try {
        const allSubjects = [contextData?.primarySubject || 'English Language', ...(contextData?.otherSubjects || [])];
        await updateSubjects({
          subjects: allSubjects,
          predicted_grade: predictedGrade,
        });
        setContextData({ ...contextData, predictedGrade });
        goToStep('aspirational-grade');
      } catch (error) {
        console.error('Failed to save predicted grade:', error);
        setContextData({ ...contextData, predictedGrade });
        goToStep('aspirational-grade');
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <div className="w-full max-w-[600px] flex flex-col gap-8 px-6">
        {/* Progress bar */}
        <div className="flex gap-2">
          <Progress className="flex-1 h-2" color="secondary" value={75} max={100} />
        </div>

        <div className="flex flex-col gap-6">
          <h1 className="text-2xl font-varela font-normal leading-[117%]">Predicted grade</h1>

          <div className="flex flex-col gap-2">
            <label className="text-sm">Predicted grade</label>
            <div className="relative">
              <SelectInput
                value={predictedGrade}
                onChange={(value) => setPredictedGrade(value)}
                options={[
                  { value: '', label: 'From 1 to 9' },
                  ...GRADES.map((grade) => ({
                    value: grade,
                    label: grade,
                  })),
                ]}
                labelClassName="[&>svg]:hidden"
                bordered
              />
              <ArrowDownIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
            </div>
          </div>

          <div className="mt-4">
            <Button onClick={handleNext} color="primary" className="w-full" disabled={isPending || !predictedGrade}>
              {isPending ? 'Saving...' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </BrandCardSideContent>
  );
}

function StepAspirationalGrade() {
  const navigate = useNavigate();
  const { contextData } = useStepperContext<typeof steps, OnboardingSchema>();
  const [aspirationalGrade, setAspirationalGrade] = useState(contextData?.aspirationalGrade || '');
  const { mutateAsync: updateSubjects, isPending } = useUpdateSubjects();

  const handleNext = () => {
    void (async () => {
      try {
        const allSubjects = [contextData?.primarySubject || 'English Language', ...(contextData?.otherSubjects || [])];
        await updateSubjects({
          subjects: allSubjects,
          predicted_grade: contextData?.predictedGrade,
          aspirational_grade: aspirationalGrade,
        });
        navigate(Routes.DASHBOARD);
      } catch (error) {
        console.error('Failed to save aspirational grade:', error);
        navigate(Routes.DASHBOARD);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <div className="w-full max-w-[600px] flex flex-col gap-8 px-6">
        {/* Progress bar */}
        <div className="flex gap-2">
          <Progress className="flex-1 h-2" color="secondary" value={100} max={100} />
        </div>

        <div className="flex flex-col gap-6">
          <h1 className="text-2xl font-varela font-normal leading-[117%]">Aspirational grade</h1>

          <div className="flex flex-col gap-2">
            <label className="text-sm">Aspirational grade</label>
            <div className="relative">
              <SelectInput
                value={aspirationalGrade}
                onChange={(value) => setAspirationalGrade(value)}
                options={[
                  { value: '', label: 'From 1 to 9' },
                  ...GRADES.map((grade) => ({
                    value: grade,
                    label: grade,
                  })),
                ]}
                labelClassName="[&>svg]:hidden"
                bordered
              />
              <ArrowDownIcon className="w-5 h-5 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600" />
            </div>
          </div>

          <div className="mt-4">
            <Button onClick={handleNext} color="primary" className="w-full" disabled={isPending || !aspirationalGrade}>
              {isPending ? 'Saving...' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </BrandCardSideContent>
  );
}
